<script setup>

import { onMounted, ref } from 'vue'
import api from '@/services/api'
import Icon from '@/components/Icon/Icon.vue'
import ProgressSpinner from 'primevue/progressspinner'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import amountService from '@/services/amount.service'
import moment from 'moment'

const loadedComponent = ref(false)
const rents = ref([])
const amounts = ref([])
const year = ref(moment())

const showPaid = (data, month) => {
    if(moment([year.value.format('YYYY'), month - 1, 1]).diff(moment(data.rg_started_at), 'months', true) <= -1
        || moment(data.rg_ended_at).diff(moment([year.value.format('YYYY'), month - 1, 1]), 'months', true) < 0) {
        return 'hidden'
    }

    if(data.paidByMonth[month - 1].isEmpty) {
        return 'bg-slate-200'
    }

    if(data.paidByMonth[month - 1].paid  >= data.paidByMonth[month - 1].to_pay) {
        return 'bg-lime-400'
    } else if(data.paidByMonth[month - 1].paid > 0) {
        return 'bg-amber-300'
    }
    
    return 'bg-red-600'
}

const update = () => {
    rents.value = []

    api.get('/renting/property', {'params': {
        status: 1,
    }}).then(response => {
        response.data.rentings.forEach((item) => {
            rents.value.push({
                uuid: item.uuid,
                p_name: item.p.name,
                rg_name: item.name,
                rg_started_at: item.started_at,
                rg_ended_at: item.ended_at,
                paidByMonth:  Array.from({length: 12}, _ => ({
                    paid: 0,
                    to_pay: 0,
                    isEmpty: true,
                }))
            })
        })

        amountService.get({'params': {
            'date:gte': year.value.format('YYYY') + '-01-01',
            'date:lte': year.value.format('YYYY') + '-12-31',
            'status:eq:and:rtg': 1,
            fields: "rtg(uuid,name,started_at,ended_at),prp(name)",
        }}).then(data => {
            amounts.value = data

            amounts.value.forEach((item) => {
                let found = rents.value.find((i, o) => {
                    if (i.uuid == item.rtg.uuid) {
                        return true
                    }
                })

                let dt = moment(item.date).format('M') - 1

                found.paidByMonth[dt].isEmpty = false
                found.paidByMonth[dt].paid = parseInt(found.paidByMonth[dt].paid) + parseInt(item.paid)
                found.paidByMonth[dt].to_pay = parseInt(found.paidByMonth[dt].to_pay) + parseInt(item.to_pay)
            })

            rents.value.sort((a, b) => {
                const aDate = moment(a.rg_ended_at);
                const bDate = moment(b.rg_ended_at);

                if (aDate.isValid() && !bDate.isValid()) {
                    return -1
                }
                if (!aDate.isValid() && bDate.isValid()) {
                    return 1
                }
                if (!aDate.isValid() && !bDate.isValid()) {
                    return 0
                }

                if (aDate < bDate) {
                    return -1
                }
                if (aDate > bDate) {
                    return 1
                }

                return 0;
            })

            loadedComponent.value = true
        })
    })
}

const changeSubYear = () => {
    loadedComponent.value = false

    year.value.subtract(1,'y')

    update()
}

const changeAddYear = () => {
    loadedComponent.value = false

    year.value.add(1,'y')

    update()
}

const showDate = (date) => {
    const d = moment(date)

    if(d.isValid()) {
        return d.format('DD MMM YYYY')
    }

    return ''
}

onMounted(() => {
    update()
})

</script>

<template>

<div class="col-span-2 flex flex-col border border-slate-200 p-4">
    <h3 class="m-auto text-2xl p-2 flex justify-center items-center">
        Montants des locations
        <a @click="changeSubYear()" class="cursor-pointer mx-2 my-auto">
            <Icon name="circle-left" :size="12" color="black" class="w-4 h-4 mb-1" />
        </a>
        {{ year.format('YYYY') }}
        <a @click="changeAddYear()" class="cursor-pointer mx-2 my-auto">
            <Icon name="circle-right" :size="12" color="black" class="w-4 h-4 mb-1" />
        </a>
    </h3>
    <div v-if="loadedComponent" class="mt-2 line">
        <DataTable
            :value="rents"
            size="small"
            removableSort
            tableStyle="min-width: 40rem"
        >
            <template #empty>
                <span class="flex justify-center w-full p-2">
                    Aucune location
                </span>
            </template>

            <Column header="Nom" sortable>
                <template #body="slotProps">
                    <div class="font-bold">
                        {{ slotProps.data.p_name }}<span v-if="slotProps.data.rg_name"> / {{ slotProps.data.rg_name }}</span>
                    </div>
                </template>
            </Column>

            <Column field="rg_started_at" header="Début">
                <template #body="slotProps">
                    {{ showDate(slotProps.data.rg_started_at) }}
                </template>
            </Column>
            <Column field="rg_ended_at" header="Fin">
                <template #body="slotProps">
                    {{ showDate(slotProps.data.rg_ended_at) }}
                </template>
            </Column>

            <Column field="paidByMonth[0]" header="Jan">
                <template #body="slotProps">
                    <div class="w-3 h-3 rounded-xl mx-auto" :class="showPaid(slotProps.data, 1)"></div>
                </template>
            </Column>
            <Column field="paidByMonth[1]" header="Fév">
                <template #body="slotProps">
                    <div class="w-3 h-3 rounded-xl mx-auto" :class="showPaid(slotProps.data, 2)"></div>
                </template>
            </Column>
            <Column field="paidByMonth[2]" header="Mar">
                <template #body="slotProps">
                    <div class="w-3 h-3 rounded-xl mx-auto" :class="showPaid(slotProps.data, 3)"></div>
                </template>
            </Column>
            <Column field="paidByMonth[3]" header="Avr">
                <template #body="slotProps">
                    <div class="w-3 h-3 rounded-xl mx-auto" :class="showPaid(slotProps.data, 4)"></div>
                </template>
            </Column>
            <Column field="paidByMonth[4]" header="Mai">
                <template #body="slotProps">
                    <div class="w-3 h-3 rounded-xl mx-auto" :class="showPaid(slotProps.data, 5)"></div>
                </template>
            </Column>
            <Column field="paidByMonth[5]" header="Jun">
                <template #body="slotProps">
                    <div class="w-3 h-3 rounded-xl mx-auto" :class="showPaid(slotProps.data, 6)"></div>
                </template>
            </Column>
            <Column field="paidByMonth[6]" header="Jul">
                <template #body="slotProps">
                    <div class="w-3 h-3 rounded-xl mx-auto" :class="showPaid(slotProps.data, 7)"></div>
                </template>
            </Column>
            <Column field="paidByMonth[7]" header="Aoû">
                <template #body="slotProps">
                    <div class="w-3 h-3 rounded-xl mx-auto" :class="showPaid(slotProps.data, 8)"></div>
                </template>
            </Column>
            <Column field="paidByMonth[8]" header="Sep">
                <template #body="slotProps">
                    <div class="w-3 h-3 rounded-xl mx-auto" :class="showPaid(slotProps.data, 9)"></div>
                </template>
            </Column>
            <Column field="paidByMonth[9]" header="Oct">
                <template #body="slotProps">
                    <div class="w-3 h-3 rounded-xl mx-auto" :class="showPaid(slotProps.data, 10)"></div>
                </template>
            </Column>
            <Column field="paidByMonth[10]" header="Nov">
                <template #body="slotProps">
                    <div class="w-3 h-3 rounded-xl mx-auto" :class="showPaid(slotProps.data, 11)"></div>
                </template>
            </Column>
            <Column field="paidByMonth[11]" header="Déc">
                <template #body="slotProps">
                    <div class="w-3 h-3 rounded-xl mx-auto" :class="showPaid(slotProps.data, 12)"></div>
                </template>
            </Column>
        </DataTable>
    </div>
    <div v-else class="mx-4 w-full flex justify-center">
        <ProgressSpinner
            style="width: 50px; height: 50px"
            strokeWidth="5"
            fill="transparent"
            animationDuration="2.5s"
            aria-label="Custom ProgressSpinner"
            class="m-auto"
        />
    </div>
</div>

</template>
